import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { fleet } from "@/api/client";
import { PageHead } from "@/components/PageHead";
import { CollapsibleSection } from "@/components/Section";
import { AssetDetail } from "@/modules/fleet/AssetDetail";
import { NewRun } from "@/modules/fleet/NewRun";
import { RunCard } from "@/modules/fleet/RunCard";
import { POLL } from "@/lib/format";

/** Fleet — one page: choose a command, choose who it applies to, run it.
 *
 *  WHY ONE PAGE. It was two subpages, Commands and Asset Control, and
 *  Dattatray rejected that: *"Asset control subpage doe snot make any
 *  sesnse!"* He was right, and the reason is the one that killed the split —
 *  he had just asked for the target selector to have **two parts, a single
 *  asset with the same filters and a bulk apply**. Once the command page can
 *  target one asset, a separate page for one asset is a second way to do one
 *  thing. D-101's rule, applied the other direction: one decision, one place.
 *
 *  THE ORDER IS THE FIX TO THE OLD COMMAND BOX, which he also called out as
 *  "entirely incoorect". It used to show an "Immobilise as" dropdown
 *  unconditionally — present and meaningless when you intended to mobilise —
 *  next to four controls with no primary among them. Now the COMMAND is
 *  chosen first, and everything downstream follows from its catalogue entry:
 *  its options appear only if it has any, and there is exactly ONE action
 *  button, carrying that command's label, colour and icon.
 *
 *  POLL CADENCE: the per-asset view and the batch rollups are operational
 *  state (POLL.STATE); the fleet-wide log is a list a human reads
 *  (POLL.LIST). Budgeting them differently is the UI performance law, not a
 *  preference — two 2 s polls on one page is how the console earned it.
 */

export function FleetPage() {
  const [params] = useSearchParams();
  const qc = useQueryClient();

  //: WHICH COMMAND. Everything below reads from this entry rather than
  //: deciding for itself, so a new catalogue row needs no edit here.

  const [assetId, setAssetId] = useState("");
  //: the picker's own selection. A hold belongs to the ASSET (D-35: it
  //: outlives the box), so this only drives the picker and warns when a
  //: search named a stream with no asset behind it.
  //: "" means "let the server pick the default for this action" — a sentinel
  //: rather than a number, so the UI never has to restate 24h/15min as its
  //: own constant and drift from the server's.

  // `?asset=` is how the fleet map hands an asset over, the same shape the
  // Window and History pages use for `?imei=`.
  //
  // IT STAYS IN THE URL, and that is the fix to the whole single-asset path.
  // It used to be read once and then DELETED (`p.delete("asset")`), which cost
  // three things at once: a refresh dropped the focus, nothing afterwards
  // could tell "arrived for one vehicle" from "an asset happens to be loaded",
  // and so the page had no MODE to render - only a fleet-wide builder with
  // some fields pre-filled. The parameter IS the mode; throwing it away was
  // throwing away the only thing that knew.
  const deep = params.get("asset");
  const focused = Boolean(deep && deep.trim());
  useEffect(() => {
    if (deep) setAssetId(deep);
  }, [deep]);

  const q = useQuery({
    queryKey: ["fleet", "asset", assetId],
    queryFn: () => fleet.asset(assetId),
    enabled: assetId.trim().length > 0,
    refetchInterval: POLL.STATE,
  });
  const batches = useQuery({
    queryKey: ["fleet", "batches"],
    queryFn: () => fleet.batches(),
    refetchInterval: POLL.STATE,
  });

  const v = q.data;
  //: FOTA's split: archived runs leave the active list but keep their state.
  const allRuns = batches.data?.batches ?? [];
  const liveRuns = allRuns.filter((b) => !b.archived_at);
  const archivedRuns = allRuns.filter((b) => b.archived_at);
  //: a command is ARCHIVED once it can no longer change — the same definition
  //: `COMMAND_TERMINAL` uses on the server, so the two halves agree

  // AVAILABLE ON THE FIRST RENDER, from the URL - not from the asset query.
  // It used to be derived from `v`, so the page painted the UNSCOPED fleet
  // picker first and jumped once the fetch landed: company set, search set,
  // selection replaced, and anything typed in that window overwritten. The id
  // is in the URL from the start, and the company is only for display.
  const focus = focused
    ? { assetId: deep as string, company: v?.tenant_id ?? "" }
    : null;

  // ONE ASSET: ONLY THE RUN. Dattatray: *"we only show run when we nagivate
  // from the asset data and make sure that that exact same asset is ready for
  // a command"*. The fleet-wide apparatus - the asset's own detail block,
  // Active runs, Archived runs - answers questions nobody arriving from a map
  // pin is asking, and the pickers choose a vehicle that is already chosen.
  if (focused) {
    return (
      <div className="mpage on">
        <PageHead eyebrow="Fleet · Commands" title={deep as string}
          desc="Command this one asset. Nothing is sent until you create the
                run and press Start."
          right={
            // THE WAY OUT. Without it a focused visit is a dead end: the
            // fleet view is reachable only by editing the address bar, which
            // is not a route an operator will find.
            <Link className="btn ghost" to="/fleet/commands">
              Command the fleet
            </Link>} />

        {assetId && q.isError && <AssetError error={q.error} />}

        {/* THE ASSET'S OWN STATE STAYS, and this is the one thing I will not
            strip to honour "only show run". It is what "ready for a command"
            MEANS: whether a hold already stands, whether a command is in
            flight that a new one would SUPERSEDE, and whether the vehicle has
            a box fitted at all. Commanding without it is commanding blind.
            Eleven render tests cover exactly this block, and `?asset=` is its
            only entry point - dropping it here would have deleted the
            supersede warning from the product, not just from a page. */}
        {v && <AssetDetail v={v} />}

        {/* ...and nothing else. No Active runs, no Archived runs, no Owner /
            Asset type / Find / Lifecycle pickers: a fleet-wide apparatus
            answering questions nobody arriving from a map pin is asking, and
            four controls to choose a vehicle that is already chosen. */}
        <div className="section-label">New run</div>
        <NewRun onCreated={() => qc.invalidateQueries({ queryKey: ["fleet"] })}
          focus={focus} />
      </div>
    );
  }

  return (
    <div className="mpage on">
      <PageHead eyebrow="Fleet · Commands" title="Command the fleet" />

      {/* "COMMAND ONE ASSET" IS GONE. Dattatray: *"This is not required"*.
          A run of one asset is just a run - tick a single row in New run and
          press Create - so a second way to command one vehicle was the same
          duplication that put two command pickers on this page. FOTA has no
          per-device command either.

          The ASSET DETAIL below stays: it is a READ, reached from a row in
          the log or from the map's `?asset=` link, and it answers the
          question a run cannot - what is in force on this one vehicle. */}
      {/* 3 - THE ASSET ITSELF, once one is chosen. */}
      {assetId && q.isError && <AssetError error={q.error} />}
      {v && <AssetDetail v={v} />}

      {/* NEW RUN, then ACTIVE, then ARCHIVED - FOTA's page order, because
          the question an operator arrives with is "start one", and the
          question they come back with is "how is it going". */}
      <div className="section-label">New run</div>
      {/* THE MAP'S LINK LANDS HERE. `?asset=` scopes the builder to that one
          vehicle rather than opening a second place to command it. */}
      <NewRun onCreated={() => qc.invalidateQueries({ queryKey: ["fleet"] })}
        focus={null} />

      <div style={{ marginTop: 18 }}>
        <CollapsibleSection title="Active runs" count={liveRuns.length} defaultOpen>
          {liveRuns.length === 0
            ? <div className="panel panel-pad cap">No runs yet.</div>
            : liveRuns.map((b) => <RunCard key={b.batch_id} b={b} />)}
        </CollapsibleSection>
        {archivedRuns.length > 0 && (
          <CollapsibleSection title="Archived runs" count={archivedRuns.length}>
            {archivedRuns.map((b) => <RunCard key={b.batch_id} b={b} />)}
          </CollapsibleSection>
        )}
      </div>

    </div>
  );
}

/** Why the asset could not be loaded, in the words that send the reader to the
 *  right place.
 *
 *  LIFTED OUT so the fleet view and the single-asset view cannot drift: this
 *  panel was written inline in one of them, and the other would have grown its
 *  own copy or - worse - no error at all, leaving a focused visit to a missing
 *  asset looking like an empty page.
 */
function AssetError({ error }: { error: unknown }) {
  const msg = String((error as Error)?.message ?? "");
  return (
    <div className="panel panel-pad" style={{ marginTop: 14,
          borderLeft: "3px solid var(--bad)" }}>
      {/* SAY WHICH FAILURE IT WAS. This read "No such asset, or it is not
          yours" for EVERY error, including a 500 - so a crash in
          `fota_history` presented to a super admin as a permissions problem
          on an asset he owns, and he went looking at scoping. A wrong
          diagnosis costs more than no message: it sends the reader somewhere
          else entirely. */}
      {/NOT_FOUND|NO_SUCH_ASSET|404/.test(msg)
        ? <>No such asset, or it is not yours.</>
        : <>
            <b>That asset could not be loaded.</b>{" "}
            <span className="mono" style={{ fontSize: 11.5 }}>
              {msg || "unknown error"}
            </span>
            <div style={{ fontSize: 11.5, color: "var(--t-mute)", marginTop: 6 }}>
              This is a fault on our side, not a permissions problem. The asset
              may well exist and be yours.
            </div>
          </>}
    </div>
  );
}


/** "Immobilise · Freewheel (throttle cut)" — one spelling, used by the cell
 *  and by its own tooltip, so a truncated cell and its hover cannot disagree. */
